import dayjs from "dayjs";
import uniqBy from "lodash/uniqBy";
import { Config } from "@/app/config";
import { catchCalculatorError, resultListFilter } from "@/component/assets/AbstractListingCalculator/domain";
import type { MarketingFilter, ResultListResponse } from "@/component/assets/AbstractListingCalculator/types";
import type { StringPair } from "@/shared/lib/JsonDataBuilder";
import { JsonDataBuilder } from "@/shared/lib/JsonDataBuilder";
import { http } from "@/shared/ui";
import { formatText } from "@/shared/ui/format";
import type { LoanGoal, PersonalLoanLeadCalcRequest, PersonalLoanLeadCalcResponse } from "../LeadForm/types";
import { DEFAULT_PARAMS, DEFAULT_PARAMS_SBERBANK } from "./data";
import { convertCetelemData } from "./domain/cetelem";
import { convertCibData } from "./domain/cib";
import { convertErsteData } from "./domain/erste";
import { convertKhData } from "./domain/kh";
import { convertMbhData } from "./domain/mbh";
import { convertProvidentData } from "./domain/provident";
import type {
	PersonalLoanCalculatorVariant,
	PersonalLoanRequest,
	PersonalLoanResponseItem,
	PersonalLoanSearchParams,
	PersonalLoanSort,
	ResponseListing,
} from "./types";

export function searchParamsToRequest(
	searchParams: PersonalLoanSearchParams,
	sort: PersonalLoanSort = "APR",
	variant?: PersonalLoanCalculatorVariant,
): PersonalLoanRequest {
	const { loanGoal, income, receiptOfIncome, employmentMonths, ...other } = searchParams;

	const loanGoals = ["SZABAD_FELHASZNALAS"] as LoanGoal[];

	if (loanGoal && !loanGoals.includes(searchParams.loanGoal)) {
		loanGoals.push(searchParams.loanGoal);
	}

	if (variant === "CREDIT_EXCHANGE") {
		loanGoals.push("IDEGEN_BANKI_HITELKIVALTAS", "SAJAT_BANKI_HITELKIVALTAS");
	}

	return {
		...other,
		loanGoals: loanGoals.reverse(),
		incomeList: [
			{
				incomeType: "SALARY-H",
				incomeTypeName: "Magyar alkalmazotti jövedelem",
				amount: income,
				receiptAmount: receiptOfIncome === true ? income : 0,
				durationMonths: employmentMonths,
				isCash: false,
				incomeStart: dayjs().add(-employmentMonths, "month").format("YYYY-MM-DD"),
				employmentInfo: {
					employmentType: "INDEFINIT",
					probationaryPeriodEnded: employmentMonths >= 3,
				},
			},
		],
		employmentMonths,
		hasPermanentHungarianAddress: true,
		age: 30,
		roleCode: "BM",
		comparator: sort,
	};
}

export function converterGenerator(
	request: PersonalLoanRequest,
	onlyCt: boolean,
): (r: PersonalLoanResponseItem) => PersonalLoanResponseItem {
	return (resp: PersonalLoanResponseItem) => {
		let response: PersonalLoanResponseItem = { ...resp };

		switch (response.bank.code) {
			case "cetelem":
				response = convertCetelemData(request, response);
				break;
			case "cib":
				response = convertCibData(request, response);
				break;
			case "erste":
				response = convertErsteData(request, response);
				break;
			case "kh":
				response = convertKhData(request, response);
				break;
			case "provident":
				response = convertProvidentData(request, response);
				break;
			case "mbh":
				response = convertMbhData(request, response);
				break;
			default:
		}

		// Amennyiben a futamidő kisebb vagy egyenlő mint a termék kamatperiódusa, úgy az a végig fix beállításra is jöjjön elő.
		const interestPeriods: { code: string; value: number }[] = [
			{ code: "THREE_MONTH", value: 0 },
			{ code: "ONE_YEAR", value: 1 },
			{ code: "THREE_YEAR", value: 3 },
			{ code: "FIVE_YEAR", value: 5 },
			{ code: "TEN_YEAR", value: 10 },
		];

		response.relativeinterestPeriodName = formatText(response.interestPeriod, "interest");

		if (response.maturityYears <= interestPeriods.find((p) => p.code === response.interestPeriod)?.value) {
			response.relativeinterestPeriodName = "rögzített hitelkamat";
		}

		// Moratórium átalakítás
		response.subTypeMarking = response.subType;

		if (
			!(onlyCt || response.marketing?.highlight) &&
			response.marketing?.newTargetUrl &&
			response.bank?.code !== "granit"
		) {
			response.marketing.newTargetUrl = undefined;
		}

		return response;
	};
}

export function responseProcessor(
	_request: PersonalLoanRequest,
	responseList: PersonalLoanResponseItem[],
	onePerBank: boolean,
): PersonalLoanResponseItem[] {
	if (onePerBank) {
		return uniqBy(responseList, (result) => result.bank.code);
	}

	return responseList;
}

export async function calculate(request: PersonalLoanRequest): Promise<any> {
	return http.post(Config.getApiUrl("/api/public/personal-loans"), request).catch(catchCalculatorError);
}

export function responseToResult(
	request: PersonalLoanRequest,
	response: ResponseListing,
	onePerBank: boolean,
	marketingFilter: MarketingFilter,
): ResultListResponse<PersonalLoanResponseItem> {
	return resultListFilter(
		{
			highlightList: responseProcessor(request, response.highlightList, onePerBank),
			normalList: responseProcessor(request, response.normalList.content, onePerBank),
		},
		marketingFilter,
		converterGenerator(request, marketingFilter === "ct-only"),
	);
}

export function getOtherData(values: Record<string, any>): JsonDataBuilder {
	const income = values.incomeList?.length ? values.incomeList[0] : undefined;

	const otherData = new JsonDataBuilder();

	otherData.addItem("Hitelcél", "loanGoal", values.loanGoal);
	otherData.addItem("Születés dátuma", "birthDate", values.birthDate);
	otherData.addItem("Elsődleges számlavezető bank", "currentBank", values.currentBank);
	otherData.addItem("Jövedelmek", "incomeList", values.incomeList);
	otherData.addItem("Szerepelsz a negatív KHR (BAR) listában", "inKhr", values.inKhr);
	otherData.addItem("Meglévő hitelek", "existingLoans", values.existingLoans);
	otherData.addItem("Fióki látogatás nélkül igénylem", "wantsWithOnlineService", values.wantsWithOnlineService);
	otherData.addItem(
		"Munkaviszony hossza",
		"durationMonths",
		income?.durationMonths ? `${income.durationMonths} hónap` : undefined,
	);
	otherData.addItem("Gyors ügyintézés", "fasterAdministration", values.fasterAdministration, "bool");

	return otherData;
}

export function getLeadCalculatorRequest(searchParams: PersonalLoanSearchParams): PersonalLoanLeadCalcRequest {
	const { incomeList, loanAmount, maturityYears, age, loanGoals } = searchParamsToRequest(searchParams);
	const income = incomeList?.length ? incomeList[0] : undefined;

	return {
		age,
		loanGoals,
		inKHR: undefined,
		employmentInfo: income?.employmentInfo,
		incomeList,
		creditLine: undefined,
		productRequisitionTime: undefined,

		minEmploymentRelationship: undefined,
		loanAmount,
		maturityYears,
	};
}

export function getLeadCalculatorResponse(response: PersonalLoanResponseItem): PersonalLoanLeadCalcResponse {
	const { subType, apr, startInstallment, totalRedemption, loanGoal, interestPeriod, bank, name, id } = response;

	return {
		id,
		productName: subType ? `${name} - ${subType}` : name,
		bankCode: bank?.code,
		bankName: bank?.name,
		apr,
		startInstallment,
		totalRedemption,
		loanGoal,
		interestPeriod,
	};
}

export function getInterestName(interest: string, short = false): string {
	switch (interest) {
		case "THREE_MONTH":
			return "3 hónap";
		case "SIX_MONTH":
			return "6 hónap";
		case "ONE_YEAR":
			return "1 év";
		case "THREE_YEAR":
			return "3 év";
		case "FIVE_YEAR":
			return "5 év";
		case "TEN_YEAR":
			return "10 év";
		case "FIX":
			if (short) {
				return "Végig";
			}

			return "Végig fix";

		default:
			return "Egyéb";
	}
}

export async function exportXls(searchParams: PersonalLoanSearchParams): Promise<string> {
	const request: PersonalLoanRequest = searchParamsToRequest(searchParams);

	return http.post(Config.getApiUrl("/api/private/personal-loan/export"), request);
}

export const PersonalLoanInfo = {
	getSearchParametersText: (parameters: PersonalLoanSearchParams): StringPair[] => {
		const data = new JsonDataBuilder();
		data.addTextItem("Hitelösszeg", parameters.loanAmount, "ft");
		data.addTextItem("Futamidő", parameters.maturityYears, "év");

		return data.getDataArray();
	},

	getProductParametersText: (
		_parameters: PersonalLoanSearchParams,
		response: PersonalLoanResponseItem,
	): StringPair[] => {
		const data = new JsonDataBuilder();
		data.addTextItem("Induló havi törlesztőrészlet", response.startInstallment, "ft");
		data.addTextItem("THM", response.apr, "%");
		data.addTextItem("Teljes visszafizetés", response.totalRedemption, "ft");
		data.addTextItem("Fix törlesztő", response.interestPeriod, "interestPeriods");

		return data.getDataArray();
	},
};

export function getDefaultParams(variant: PersonalLoanCalculatorVariant): PersonalLoanSearchParams {
	switch (variant) {
		case "SBERBANK":
			return DEFAULT_PARAMS_SBERBANK;

		default:
			return DEFAULT_PARAMS;
	}
}
